import { forwardRef } from "react";
import styled from "@emotion/styled";
import { Card } from "@/components/primitives/Card";
import { SectionHeader } from "../SectionHeader";
import type { FieldLabelProps, FormSectionProps, HelperTextProps, InlineErrorProps } from "./FormSection.types";

/* Exact compiled output of the pre-migration Tailwind utilities. */

const FormSectionRoot = styled(Card, {
  label: "AgenticFormSection-root"
})({
  overflow: "hidden"
});

const FormSectionHeader = styled("div", {
  label: "AgenticFormSection-header"
})({
  borderBottomWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  paddingLeft: "1rem",
  paddingRight: "1rem",
  paddingTop: ".75rem",
  paddingBottom: ".75rem"
});

const FormSectionErrorSummary = styled("div", {
  label: "AgenticFormSection-errorSummary"
})({
  borderBottomWidth: "1px",
  borderColor: "hsl(var(--bfrs-color-danger) / .2)",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-danger-soft) / var(--tw-bg-opacity, 1))",
  paddingLeft: "1rem",
  paddingRight: "1rem",
  paddingTop: ".75rem",
  paddingBottom: ".75rem",
  fontSize: ".875rem",
  lineHeight: "1.25rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-danger-contrast) / var(--tw-text-opacity, 1))"
});

const FormSectionBody = styled("div", {
  label: "AgenticFormSection-body"
})({
  display: "grid",
  gap: "1rem",
  paddingLeft: "1rem",
  paddingRight: "1rem",
  paddingTop: "1rem",
  paddingBottom: "1rem"
});

const FormSectionFooter = styled("div", {
  label: "AgenticFormSection-footer"
})({
  borderTopWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  backgroundColor: "hsl(var(--bfrs-color-surface-muted) / .6)",
  paddingLeft: "1rem",
  paddingRight: "1rem",
  paddingTop: ".75rem",
  paddingBottom: ".75rem"
});

export const FormSection = forwardRef<HTMLDivElement, FormSectionProps>(
  ({ className, title, description, actions, footer, errorSummary, children, ...props }, ref) => (
    <FormSectionRoot ref={ref} padding="none" className={className} {...props}>
      {(title || description || actions) && (
        <FormSectionHeader>
          <SectionHeader title={title} description={description} actions={actions} compact />
        </FormSectionHeader>
      )}
      {errorSummary && <FormSectionErrorSummary>{errorSummary}</FormSectionErrorSummary>}
      <FormSectionBody>{children}</FormSectionBody>
      {footer && <FormSectionFooter>{footer}</FormSectionFooter>}
    </FormSectionRoot>
  )
);

FormSection.displayName = "FormSection";

const FieldLabelRoot = styled("label", {
  label: "AgenticFieldLabel-root"
})({
  display: "block",
  fontSize: "12px",
  fontWeight: 600,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
});

const FieldLabelRequiredMark = styled("span", {
  label: "AgenticFieldLabel-requiredMark"
})({
  marginLeft: ".25rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-danger) / var(--tw-text-opacity, 1))"
});

export const FieldLabel = forwardRef<HTMLLabelElement, FieldLabelProps>(({ className, required, children, ...props }, ref) => (
  <FieldLabelRoot ref={ref} className={className} {...props}>
    {children}
    {required && <FieldLabelRequiredMark>*</FieldLabelRequiredMark>}
  </FieldLabelRoot>
));

FieldLabel.displayName = "FieldLabel";

const HelperTextRoot = styled("p", {
  label: "AgenticHelperText-root"
})({
  fontSize: ".75rem",
  lineHeight: "1.25rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

export const HelperText = forwardRef<HTMLParagraphElement, HelperTextProps>(({ className, ...props }, ref) => (
  <HelperTextRoot ref={ref} className={className} {...props} />
));

HelperText.displayName = "HelperText";

const InlineErrorRoot = styled("p", {
  label: "AgenticInlineError-root"
})({
  fontSize: ".75rem",
  lineHeight: "1.25rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-danger) / var(--tw-text-opacity, 1))"
});

export const InlineError = forwardRef<HTMLParagraphElement, InlineErrorProps>(({ className, ...props }, ref) => (
  <InlineErrorRoot ref={ref} className={className} {...props} />
));

InlineError.displayName = "InlineError";
